import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toProfileSlug, toBlogSlug } from '../utils/slug';
import Navbar from '../components/Navbar';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, ArrowRight, Play, TrendingUp, Users, Zap, Shield, Sparkles, Star, Mic2, MessageCircle, BarChart3, Globe, Send, ChevronRight, BookOpen, Headphones, Radio, Music } from 'lucide-react';
import { SocialLinksComponent } from '../components/Footer';
import PageLoader from '../components/PageLoader';
import SEO from '../components/SEO';
import { ActivityTable, ActivityCarousel } from '../components/LiveActivity';
import StickyHowItWorks from '../components/StickyHowItWorks';
const C = {
  bg: 'var(--pd-bg)', bg2: 'var(--pd-bg2)', surface: 'var(--pd-surface)', surface2: 'var(--pd-surface2)', surface3: 'var(--pd-surface3)',
  border: 'var(--pd-border)', text: 'var(--pd-text)', text2: 'var(--pd-text2)', muted: 'var(--pd-muted)',
  purple: 'var(--pd-purple)', cyan: 'var(--pd-cyan)', amber: 'var(--pd-amber)', green: 'var(--pd-green)', red: 'var(--pd-red)',
  gold: 'var(--pd-gold)', glowP: 'var(--pd-glow-p)', glowC: 'var(--pd-glow-c)',
  glowG: 'var(--pd-glow-g)', glass: 'var(--pd-glass)',
};

/* ─── Scroll Reveal (Disabled per request) ─── */
const useReveal = () => {
  return [{ current: null }, true];
};
const Reveal = ({ children, className = '', delay = 0, onVisible }) => {
  useEffect(() => { if (onVisible) onVisible(); }, []);
  return <div className={className}>{children}</div>;
};

/* ─── Waveform ─── */
const Waveform = ({ className = '', color = C.purple }) => (
  <div className={`flex items-end gap-[2px] h-5 ${className}`}>
    {[0.6,1,0.4,0.8,0.5,1,0.3,0.7,0.9,0.4].map((h, i) => (
      <div key={i} className="w-[2px] rounded-full origin-bottom" style={{ height: `${h*100}%`, background: color, animation: `waveBar 1.2s ease-in-out ${i*0.08}s infinite alternate` }} />
    ))}
  </div>
);

/* ─── Match Score Ring ─── */
const MatchRing = ({ score = 85, size = 48 }) => {
  const strokeW = size < 40 ? 2.5 : 3;
  const r = (size - strokeW * 2) / 2, circ = 2 * Math.PI * r, offset = circ - (score / 100) * circ;
  const fontSize = size < 40 ? 9 : size < 50 ? 11 : 13;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="var(--pd-ring-bg, rgba(255,255,255,0.06))" strokeWidth={strokeW} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={score > 80 ? C.green : score > 60 ? C.cyan : C.amber}
          strokeWidth={strokeW} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-1000" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-bold" style={{ color: C.text, fontSize }}>{score}%</span>
    </div>
  );
};

/* ─── Tier Badge (text only) ─── */
const TierBadge = ({ tier }) => {
  const cfg = {
    free: { label: 'Verified', color: C.green, bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.2)' },
    licensed: { label: 'Licensed', color: C.cyan, bg: 'rgba(34,211,238,0.1)', border: 'rgba(34,211,238,0.2)' },
    featured: { label: 'Featured', color: C.gold, bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.2)' },
    powerhouse: { label: 'Elite', color: C.purple, bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.2)' },
  }[tier] || { label: 'Verified', color: C.green, bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.2)' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold" style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
      {cfg.label}
    </span>
  );
};

/* ─── Leaderboard logic moved to LeaderboardPage ─── */


/* ════════════════ MAIN COMPONENT ════════════════ */
const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [podcasters, setPodcasters] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [activities, setActivities] = useState([]);
  const [sponsoredAds, setSponsoredAds] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [heroStats, setHeroStats] = useState({ active: 0, swaps: 0, subscribers: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      axios.get(`${API}/podcasters?limit=12`),
      axios.get(`${API}/podcasters/featured`),
      axios.get(`${API}/activities/feed?limit=8`),
      axios.get(`${API}/leaderboard?limit=30`),
      axios.get(`${API}/ads/active`),
      axios.get(`${API}/stats/public`),
      axios.get(`${API}/blogs?limit=3`),
    ]).then(([p, f, a, lb, ads, stats, blogs]) => {
      if (p.status === 'fulfilled') {
        const data = Array.isArray(p.value.data) ? p.value.data : [];
        setPodcasters(data.filter(user => !user.is_admin && user.role !== 'admin' && user.email !== 'admin@podcasters.directory' && user.name !== 'Admin User'));
      }
      if (f.status === 'fulfilled') {
        const fData = Array.isArray(f.value.data) ? f.value.data : [];
        setFeatured(fData.filter(user => !user.is_admin && user.role !== 'admin' && user.email !== 'admin@podcasters.directory' && user.name !== 'Admin User'));
      }
      if (a.status === 'fulfilled') setActivities(Array.isArray(a.value.data) ? a.value.data : []);
      if (ads.status === 'fulfilled') setSponsoredAds(Array.isArray(ads.value.data) ? ads.value.data : []);
      if (blogs.status === 'fulfilled') setBlogPosts(Array.isArray(blogs.value.data) ? blogs.value.data : []);
      if (stats.status === 'fulfilled') {
        const st = stats.value.data?.totals || {};
        const tiers = stats.value.data?.tiers || {};
        const paidSubs = (tiers.licensed || 0) + (tiers.featured || 0) + (tiers.powerhouse || 0);
        setHeroStats({
          active: st.podcasters || 0,
          swaps: st.matches_accepted || 0,
          subscribers: paidSubs,
        });
      }
      setLoading(false);
    });
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div className="min-h-screen" style={{ background: "transparent", fontFamily: "'Inter', sans-serif" }} data-testid="homepage">
      <SEO path="/" jsonLd={{
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Podcasters.Directory",
        "url": "https://podcasters.directory",
        "description": "The #1 podcast host and guest marketplace.",
        "potentialAction": { "@type": "SearchAction", "target": "https://podcasters.directory/directory?q={search_term_string}", "query-input": "required name=search_term_string" }
      }} />
      <Navbar />
      <style>{`
        .card-hover { transition: box-shadow 0.3s ease; }
        .card-hover:hover { box-shadow: var(--pd-card-shadow-hover); }
        .gold-border { border: 2px solid ${C.gold}; }
        .elite-border { border: 2px solid ${C.purple}; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ═══════════ HERO ═══════════ */}
      <div className="relative overflow-hidden">
        <div className="blob bg-purple-600/30 w-64 h-64 top-10 -left-10 mix-blend-screen" />
        <div className="blob bg-cyan-500/20 w-72 h-72 top-40 right-10 mix-blend-screen" />
        <div className="blob bg-red-500/20 w-48 h-48 bottom-10 left-1/4 mix-blend-screen" />
        <div className="blob bg-green-500/20 w-40 h-40 bottom-32 right-1/4 mix-blend-screen" />

        <section className="relative pt-36 pb-20 md:pt-48 md:pb-32 text-center" data-testid="hero-section">
          <div className="max-w-[1000px] mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl leading-[1.2] sm:text-5xl md:text-[5rem] md:leading-[1.1] font-extrabold tracking-tight mb-8" style={{ color: C.text }} data-testid="hero-title">
              {t('home.title')}{' '}
              <span className="text-gradient">{t('home.titleHighlight')}</span>{' '}
              {t('home.titleEnd')}
            </h1>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-12 sm:mb-16">
              <button className="rounded-full border border-white/20 bg-transparent text-white hover:bg-white hover:text-black transition-colors px-8 py-3 font-medium w-full sm:w-auto" onClick={() => navigate('/auth?mode=register&role=guest')}>
                Join Guest
              </button>
              <button className="rounded-full border border-white/20 bg-transparent text-white hover:bg-white hover:text-black transition-colors px-8 py-3 font-medium w-full sm:w-auto" onClick={() => navigate('/auth?mode=register&role=host')}>
                Join Host
              </button>
              <button className="rounded-full border border-white/20 bg-transparent text-white hover:bg-white hover:text-black transition-colors px-8 py-3 font-medium w-full sm:w-auto" onClick={() => navigate('/auth?mode=register&role=both')}>
                Both
              </button>
            </div>

            {/* Masonry Collage */}
            <div className="hidden md:grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-20 max-w-4xl mx-auto">
              {(() => {
                const eliteTiers = ['featured', 'powerhouse'];
                const eliteFromAll = podcasters.filter(p => eliteTiers.includes(p.subscription_tier));
                const combined = [...featured, ...eliteFromAll];
                const seen = new Set();
                const unique = combined.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; });
                unique.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
                const heroProfiles = unique.length >= 6 ? unique : podcasters.length >= 6 ? podcasters : Array(6).fill({});
                const p = heroProfiles.slice(0, 6).map((prof, i) => {
                  const defaultImages = [
                    '/hero/1.jpg',
                    '/hero/2.jpg',
                    '/hero/3.jpg',
                    '/hero/4.jpg',
                    '/hero/5.jpg',
                    '/hero/6.jpg'
                  ];
                  const avatar = prof.avatar_url;
                  const finalImg = (!avatar || avatar.includes('ui-avatars')) 
                                   ? defaultImages[i % defaultImages.length] 
                                   : avatar;
                  return {
                    id: prof.id || i,
                    name: prof.name || `Podcast Creator ${i + 1}`,
                    img: finalImg,
                    slug: toProfileSlug(prof.name || 'Podcaster', prof.id || String(i))
                  };
                });
                
                return (
                  <>
                    <div className="space-y-4 md:space-y-6 pt-12">
                      <Link to={`/profile/${p[0].slug}`}><img src={p[0].img} className="w-full aspect-[4/5] object-cover rounded-[1.5rem] md:rounded-[2rem] hover:scale-105 transition-transform" alt={`Podcast profile for ${p[0].name}`} loading="lazy" /></Link>
                    </div>
                    <div className="space-y-4 md:space-y-6">
                      <Link to={`/profile/${p[1].slug}`}><img src={p[1].img} className="w-full aspect-square object-cover rounded-[1.5rem] md:rounded-[2rem] hover:scale-105 transition-transform" alt={`Podcast profile for ${p[1].name}`} loading="lazy" /></Link>
                      <Link to={`/profile/${p[2].slug}`}><img src={p[2].img} className="w-full aspect-[4/3] object-cover rounded-[1.5rem] md:rounded-[2rem] hover:scale-105 transition-transform" alt={`Podcast profile for ${p[2].name}`} loading="lazy" /></Link>
                    </div>
                    <div className="space-y-4 md:space-y-6 col-span-2 md:col-span-1">
                      <Link to={`/profile/${p[3].slug}`}><img src={p[3].img} className="w-full aspect-[2/3] object-cover rounded-[2rem] hover:scale-105 transition-transform" alt={`Podcast profile for ${p[3].name}`} loading="lazy" /></Link>
                    </div>
                    <div className="space-y-4 md:space-y-6 pt-8 hidden md:block">
                      <Link to={`/profile/${p[4].slug}`}><img src={p[4].img} className="w-full aspect-[4/3] object-cover rounded-[2rem] hover:scale-105 transition-transform" alt={`Podcast profile for ${p[4].name}`} loading="lazy" /></Link>
                      <Link to={`/profile/${p[5].slug}`}><img src={p[5].img} className="w-full aspect-square object-cover rounded-[2rem] hover:scale-105 transition-transform" alt={`Podcast profile for ${p[5].name}`} loading="lazy" /></Link>
                    </div>
                  </>
                );
              })()}
            </div>

            {/* Platform Icons — real branded */}
            <div className="flex flex-col items-center gap-4 sm:gap-5 mt-8 sm:mt-12">
              <p className="text-sm font-medium tracking-wide" style={{ color: C.text2 }}>Listen to Podcasters through:</p>
              <div className="flex flex-wrap justify-center gap-6 sm:gap-10 items-center opacity-80 hover:opacity-100 transition-opacity">
                {/* Apple Podcasts */}
                <div title="Apple Podcasts" className="hover:scale-110 transition-transform cursor-pointer">
                  <img src="/podcast-icon/applepodcasts.svg" alt="Apple Podcasts" className="h-8 sm:h-10 w-auto" loading="lazy" />
                </div>
                {/* Spotify */}
                <div title="Spotify" className="hover:scale-110 transition-transform cursor-pointer">
                  <img src="/podcast-icon/spotify.svg" alt="Spotify" className="h-8 sm:h-10 w-auto" loading="lazy" />
                </div>
                {/* iHeartRadio */}
                <div title="iHeartRadio" className="hover:scale-110 transition-transform cursor-pointer">
                  <img src="/podcast-icon/iheartradio.svg" alt="iHeartRadio" className="h-8 sm:h-10 w-auto" loading="lazy" />
                </div>
                {/* Amazon Music */}
                <div title="Amazon Music" className="hover:scale-110 transition-transform cursor-pointer rounded-lg overflow-hidden">
                  <img src="/podcast-icon/amazon-music.jpg" alt="Amazon Music" className="h-8 sm:h-10 w-auto object-contain" loading="lazy" />
                </div>
                {/* Google Play */}
                <div title="Google Play" className="hover:scale-110 transition-transform cursor-pointer">
                  <img src="/podcast-icon/google-play.svg" alt="Google Play" className="h-8 sm:h-10 w-auto" loading="lazy" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>



      {/* ═══════════ HOW IT WORKS ═══════════ */}
      <StickyHowItWorks />

      {/* ═══════════ MATCH SCORE EXPLAINER ═══════════ */}
      <section className="min-h-[100dvh] flex flex-col justify-center py-20 md:py-28" data-testid="match-score-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <>
              <div>

                <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-6" style={{ color: C.text }}>
                  <span className="text-gradient">{t('home.smartMatchTitle')}</span> {t('home.smartMatchHighlight')}
                </h2>
                <p className="text-base leading-relaxed mb-6" style={{ color: C.text2 }}>
                  {t('home.smartMatchDesc')}
                </p>
                <div className="space-y-3">
                  {[
                    { text: t('home.matchTopic'), color: C.purple },
                    { text: t('home.matchAudience'), color: C.cyan },
                    { text: t('home.matchActivity'), color: C.green },
                    { text: t('home.matchLang'), color: C.amber },
                  ].map((f, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full shrink-0" style={{ background: f.color }} />
                      <span className="text-sm" style={{ color: C.text2 }}>{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
            <>
              <div className="rounded-3xl p-8 relative overflow-hidden">
                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.purple }}>{t('home.matchAnalysis')}</span>
                    <MatchRing score={92} size={64} />
                  </div>
                  <div className="space-y-4">
                    {[
                      { label: t('home.topicOverlap'), pct: 95, color: C.green },
                      { label: t('home.audienceFit'), pct: 88, color: C.cyan },
                      { label: t('home.expertise'), pct: 92, color: C.purple },
                      { label: t('home.availability'), pct: 85, color: C.amber },
                    ].map((bar, i) => (
                      <div key={i}>
                        <div className="flex justify-between text-xs mb-1">
                          <span style={{ color: C.text2 }}>{bar.label}</span>
                          <span className="font-semibold" style={{ color: bar.color }}>{bar.pct}%</span>
                        </div>
                        <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--pd-card-bg)' }}>
                          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${bar.pct}%`, background: bar.color }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          </div>
        </div>
      </section>

      {/* ═══════════ AI COPILOT SECTION ═══════════ */}
      <section className="min-h-[100dvh] flex flex-col justify-center py-20 md:py-28" data-testid="ai-copilot-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <>
            <div className="text-center mb-14">

              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4" style={{ color: C.text }}><span className="text-gradient">Meet</span> Your Matchmaker</h2>
              <p className="text-sm sm:text-base md:text-lg max-w-xl mx-auto" style={{ color: C.text2 }}>A friendly assistant that helps you find great podcast partners. Just tell us what you're looking for, and we'll help you connect.</p>
            </div>
          </>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left: Chat mockup */}
            <>
              <div className="rounded-3xl overflow-hidden" style={{ border: `1px solid ${C.border}` }}>
                {/* Chat header */}
                <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: C.border, background: 'rgba(139,92,246,0.04)' }}>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#8B5CF6' }}>
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" /></svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: C.text }}>Match Assistant</p>
                    <p className="text-[10px]" style={{ color: C.muted }}>Matchmaking assistant</p>
                  </div>
                  <span className="ml-auto flex items-center gap-1 text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(34,211,238,0.12)', color: C.cyan }}>Online</span>
                </div>
                {/* Chat messages */}
                <div className="p-5 space-y-4" style={{ minHeight: 280 }}>
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#8B5CF6' }}>
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3" /></svg>
                    </div>
                    <div className="rounded-xl p-3 text-xs leading-relaxed max-w-[85%]" style={{ background: 'var(--pd-card-bg)', border: `1px solid ${C.border}`, color: C.text }}>
                      Hey! I found <strong>3 great matches</strong> for your tech podcast. They're all looking for cross-promotion partners in your niche.
                    </div>
                  </div>
                  {/* Suggested profiles */}
                  <div className="ml-9 space-y-2">
                    {[
                      { name: 'Dennis P.', niche: 'Motivational & Success', score: 95, color: '#22C55E', avatar: '/dennis.png' },
                      { name: 'Carrie C.', niche: 'AI & Leadership', score: 88, color: '#22D3EE', avatar: '/carrie.png' },
                      { name: 'Ryan M.', niche: 'Tech Leadership', score: 82, color: '#F59E0B', avatar: 'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=80' },
                    ].map((p, i) => (
                      <div key={i} className="flex items-center gap-3 p-2.5 rounded-xl border transition-all" style={{ background: 'var(--pd-card-bg)', borderColor: C.border }}>
                        <img src={p.avatar} alt={p.name} className="w-8 h-8 rounded-lg object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold" style={{ color: C.text }}>{p.name}</p>
                          <p className="text-[10px]" style={{ color: C.muted }}>{p.niche}</p>
                        </div>
                        <span className="text-xs font-bold" style={{ color: p.color }}>{p.score}%</span>
                      </div>
                    ))}
                  </div>
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="rounded-xl p-3 text-xs text-white max-w-[75%]" style={{ background: '#8B5CF6', borderBottomRightRadius: 4 }}>
                      Tell me more about Dennis - why is he a 95% match?
                    </div>
                  </div>
                  <div className="flex gap-2.5">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: '#8B5CF6' }}>
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3" /></svg>
                    </div>
                    <div className="rounded-xl p-3 text-xs leading-relaxed max-w-[85%]" style={{ background: 'var(--pd-card-bg)', border: `1px solid ${C.border}`, color: C.text }}>
                      Dennis's audience overlaps <strong>78% with yours</strong>, he's actively seeking guests, and his engagement rate is in the <strong>top 5%</strong>.
                    </div>
                  </div>
                </div>
                {/* Chat input */}
                <div className="px-5 pb-4">
                  <div className="flex gap-2 rounded-xl p-1" style={{ background: 'var(--pd-card-bg)', border: `1px solid ${C.border}` }}>
                    <div className="flex-1 px-3 py-2 text-xs" style={{ color: C.muted }}>Ask about matches...</div>
                    <div className="px-3 py-2 rounded-lg" style={{ background: C.purple }}>
                      <Send className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            </>

            {/* Right: Feature highlights */}
            <>
              <div className="space-y-6">
                {[
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>
                    ),
                    title: 'Easy Discovery',
                    desc: 'Tell the assistant what you need - "find me tech podcast hosts with 10k+ listeners" - and get instant, ranked results.',
                    color: C.cyan,
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg>
                    ),
                    title: 'Instant Match Scoring',
                    desc: 'Every suggestion comes with a match percentage based on audience overlap, niche alignment, and engagement data.',
                    color: C.purple,
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.25 8.511c.884.284 1.5 1.128 1.5 2.097v4.286c0 1.136-.847 2.1-1.98 2.193-.34.027-.68.052-1.02.072v3.091l-3-3c-1.354 0-2.694-.055-4.02-.163a2.115 2.115 0 01-.825-.242m9.345-8.334a2.126 2.126 0 00-.476-.095 48.64 48.64 0 00-8.048 0c-1.131.094-1.976 1.057-1.976 2.192v4.286c0 .837.46 1.58 1.155 1.951m9.345-8.334V6.637c0-1.621-1.152-3.026-2.76-3.235A48.455 48.455 0 0011.25 3c-2.115 0-4.198.137-6.24.402-1.608.209-2.76 1.614-2.76 3.235v6.226c0 1.621 1.152 3.026 2.76 3.235.577.075 1.157.14 1.74.194V21l4.155-4.155" /></svg>
                    ),
                    title: 'Chat Naturally',
                    desc: 'Chat naturally - ask follow-up questions, refine your search, or get deeper insights on any suggested match.',
                    color: C.green,
                  },
                  {
                    icon: (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" /></svg>
                    ),
                    title: 'One-Click Outreach',
                    desc: 'Found your match? Send a swap request directly from the chat - no extra steps, no switching screens.',
                    color: C.gold,
                  },
                ].map((f, i) => (
                  <div key={i} className="flex gap-4 items-start group">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-" style={{ background: `${f.color}15`, border: `1px solid ${f.color}30`, color: f.color }}>
                      {f.icon}
                    </div>
                    <div>
                      <h3 className="text-base font-semibold mb-1" style={{ color: C.text }}>{f.title}</h3>
                      <p className="text-sm leading-relaxed" style={{ color: C.text2 }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
                <Link to="/auth" className="inline-flex items-center gap-2 mt-4 px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all hover:scale-[1.02]" style={{ background: '#8B5CF6' }} data-testid="copilot-cta-btn">
                  Try the Assistant Free <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
          </div>
        </div>
      </section>

      {/* ═══════════ COMMUNITY & CHAT PREVIEW ═══════════ */}
      <section className="min-h-[100dvh] flex flex-col justify-center py-20 md:py-28" data-testid="community-chat-section">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <>
            <div className="text-center mb-14">
              <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4" style={{ color: C.text }}><span className="text-gradient">{t('home.communityTitle').split(' ')[0]}</span> {t('home.communityTitle').split(' ').slice(1).join(' ')}</h2>
              <p className="text-sm sm:text-base md:text-lg max-w-lg mx-auto" style={{ color: C.text2 }}>{t('home.communitySub')}</p>
            </div>
          </>
          <div className="grid lg:grid-cols-2 gap-8">
            <>
              <div className="rounded-3xl p-6 h-full">
                <div className="flex items-center gap-2 mb-5">
                  <span className="text-lg font-semibold" style={{ color: C.text }}>{t('home.directPulse')}</span>
                  <span className="ml-auto text-[10px] px-2 py-0.5 rounded" style={{ background: 'rgba(16,185,129,0.1)', color: C.green }}>{t('home.premium')}</span>
                </div>
                <div className="rounded-2xl p-4 space-y-3 mb-4" style={{ background: 'var(--pd-card-bg)' }}>
                  <div className="flex gap-3">
                    <img src="/dennis.png" className="w-8 h-8 rounded-lg object-cover" alt="" />
                    <div className="rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[80%]" style={{ background: 'rgba(139,92,246,0.12)' }}>
                      <p className="text-xs" style={{ color: C.text }}>Hi Carrie! I love your work on AI in business. Would you be open to a guest swap?</p>
                      <span className="text-[9px] block mt-1" style={{ color: C.muted }}>2:34 PM</span>
                    </div>
                  </div>
                  <div className="flex gap-3 justify-end">
                    <div className="rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%]" style={{ background: 'rgba(34,211,238,0.1)' }}>
                      <p className="text-xs" style={{ color: C.text }}>Absolutely! I've been following your show. Let's set something up!</p>
                      <span className="text-[9px] block mt-1" style={{ color: C.muted }}>2:36 PM</span>
                    </div>
                    <img src="/carrie.png" className="w-8 h-8 rounded-lg object-cover" alt="" />
                  </div>
                  <div className="flex items-center gap-2 px-2">
                    <div className="flex gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '0s' }} /><span className="w-1.5 h-1.5 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '0.15s' }} /><span className="w-1.5 h-1.5 rounded-full bg-[#94A3B8] animate-bounce" style={{ animationDelay: '0.3s' }} /></div>
                    <span className="text-[10px]" style={{ color: C.muted }}>Dennis is typing...</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 h-10 rounded-full px-4 flex items-center text-xs" style={{ background: 'var(--pd-input-bg)', border: `1px solid ${C.border}`, color: C.muted }}>
                    Type a message...
                  </div>
                  <button className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.purple }}>
                    <Send className="w-4 h-4" style={{ color: '#fff' }} />
                  </button>
                </div>
              </div>
            </>

            <>
              <div className="space-y-5">
                {[
                  { title: t('home.smartDiscovery'), desc: t('home.smartDiscoveryDesc'), color: C.purple },
                  { title: t('home.bookingPipeline'), desc: t('home.bookingPipelineDesc'), color: C.cyan },
                  { title: t('home.savedProfiles'), desc: t('home.savedProfilesDesc'), color: C.red },
                  { title: t('home.badgesTrust'), desc: t('home.badgesTrustDesc'), color: C.gold },
                ].map((f, i) => (
                  <div key={i} className="card-hover rounded-2xl p-5 flex items-start gap-4 group">
                    <div className="w-2 h-2 rounded-full shrink-0 mt-2" style={{ background: f.color }} />
                    <div>
                      <h3 className="text-sm font-semibold mb-1" style={{ color: C.text }}>{f.title}</h3>
                      <p className="text-xs leading-relaxed" style={{ color: C.text2 }}>{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </>
          </div>
        </div>
      </section>

{/* ═══════════ FEATURED PROFILES (Tier-sorted Subscribers) ═══════════ */}
      {(() => {
        const tierOrder = { powerhouse: 0, featured: 1, licensed: 2, free: 3 };
        const tierMeta = {
          powerhouse: { label: 'Elite', color: C.purple, border: C.purple, bg: 'rgba(139,92,246,0.06)' },
          featured:   { label: 'Featured', color: C.gold, border: C.gold, bg: 'rgba(251,191,36,0.06)' },
          licensed:   { label: 'Licensed Swapper', color: C.cyan, border: C.cyan, bg: 'rgba(0,243,255,0.06)' },
        };
        // Merge all data sources and deduplicate by id
        const seen = new Set();
        const allProfiles = [...featured, ...podcasters].filter(p => {
          if (!p.id || seen.has(p.id)) return false;
          seen.add(p.id);
          return true;
        });
        // Only paid subscribers, sorted by tier then ranking
        const subscribers = allProfiles
          .filter(p => p.subscription_tier && p.subscription_tier !== 'free')
          .sort((a, b) => {
            const tierDiff = (tierOrder[a.subscription_tier] || 3) - (tierOrder[b.subscription_tier] || 3);
            if (tierDiff !== 0) return tierDiff;
            return (b.ranking_score || 0) - (a.ranking_score || 0);
          });
        return (
          <section className="min-h-[100dvh] flex flex-col justify-center py-16 md:py-32" data-testid="featured-profiles-section">
            <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-12">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold" style={{ color: C.text }}><span className="text-gradient">Recent</span> Podcasters</h2>
                <Link to="/directory" className="btn-pill hover:bg-white/10 transition-colors hidden sm:flex">
                  View All Podcasters <ChevronRight className="w-4 h-4 ml-2" />
                </Link>
              </div>

              {subscribers.length === 0 ? (
                <div className="text-center py-16 rounded-3xl" style={{ border: `1px solid ${C.border}`, background: 'var(--pd-card-bg)' }}>
                  <Users className="w-10 h-10 mx-auto mb-3" style={{ color: C.muted }} />
                  <p className="text-sm font-medium" style={{ color: C.text2 }}>No subscribers yet</p>
                </div>
              ) : (
                <div className="grid lg:grid-cols-2 gap-6 md:gap-10">
                  {/* Left Column: Stacked List */}
                  <div className="space-y-4 md:space-y-6">
                    {subscribers.slice(1, 4).map((p, i) => (
                      <Link key={p.id || i} to={`/profile/${toProfileSlug(p.name, p.id || String(i))}`} className="block group">
                        <div className="relative rounded-[2rem] overflow-hidden transition-all group-hover:-translate-y-1 shadow-lg border border-white/5">
                          {/* Background Image */}
                          <img 
                            src={p.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(p.name)}&background=1a1a2e&color=8B5CF6&size=800`} 
                            alt={p.name} 
                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]/20" />
                          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent sm:hidden" />
                          
                          <div className="relative z-10 p-6 sm:p-8 flex items-center justify-between gap-6">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 sm:gap-4 mb-4 flex-wrap">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/5 text-white/80 border border-white/10 backdrop-blur-sm">Profile {i+2}</span>
                                <div className="flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full bg-green-400 shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
                                  <span className="text-sm font-medium text-white/90 drop-shadow-md">{p.name}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-sm font-semibold text-white/90 drop-shadow-md">
                                  <span className="w-2 h-2 rounded-full bg-red-400" />
                                  <span>{(p.monthly_listeners || 0).toLocaleString()} Listeners</span>
                                </div>
                              </div>
                              <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 truncate drop-shadow-lg">{p.podcast_name || p.headline || p.name}</h3>
                              <p className="text-sm text-white/70 line-clamp-2 mb-6 max-w-md drop-shadow-md">{p.bio || `Exploring ${p.category || 'podcasting'} and its impact on the industry.`}</p>
                              
                              {/* Waveform visualizer */}
                              <div className="flex items-end gap-[3px] h-8 mb-6">
                                {[0.4,0.7,0.5,1,0.6,0.8,0.3,0.9,0.5,0.7,0.4,0.6,0.8,0.3,0.5,1,0.6,0.4,0.7,0.5].map((h, wi) => (
                                  <div key={wi} className="w-[3px] rounded-full flex-shrink-0"
                                    style={{
                                      height: `${h * 100}%`,
                                      background: `rgba(255,255,255,${0.25 + h * 0.4})`,
                                      animation: `waveBar 1.4s ease-in-out ${wi * 0.07}s infinite alternate`
                                    }}
                                  />
                                ))}
                              </div>

                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 group-hover:bg-gray-200 group-hover:scale-110 transition-all duration-300">
                                  <Play className="w-5 h-5 text-black ml-1" fill="currentColor" />
                                </div>
                                <span className="text-sm font-bold text-white underline decoration-white/50 underline-offset-4 group-hover:decoration-white transition-colors drop-shadow-md">View Profile</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {/* Right Column: Featured Large Card */}
                  {subscribers[0] && (
                    <Link to={`/profile/${toProfileSlug(subscribers[0].name, subscribers[0].id)}`} className="block group rounded-[2.5rem] overflow-hidden relative min-h-[500px] lg:min-h-0">
                      <img src={subscribers[0].avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(subscribers[0].name)}&background=1a1a2e&color=8B5CF6&size=800`} alt={`Featured profile: ${subscribers[0].name}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/60 to-[#050505]/10" />
                      
                      <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
                        <span className="px-4 py-1.5 rounded-full text-xs font-bold bg-black/50 backdrop-blur-md text-white border border-white/10">Profile 1</span>
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-white/90 drop-shadow-md">
                          <span className="w-2 h-2 rounded-full bg-red-400" />
                          <span>{(subscribers[0].monthly_listeners || 0).toLocaleString()} Listeners</span>
                        </div>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 p-8 sm:p-12">
                        <div className="flex items-center gap-2 mb-4">
                          <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_10px_rgba(34,211,238,0.8)]" />
                          <span className="text-sm font-medium text-white/90 drop-shadow-md">{subscribers[0].name}</span>
                        </div>
                        <h3 className="text-3xl sm:text-4xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg">{subscribers[0].podcast_name || subscribers[0].headline || subscribers[0].name}</h3>
                        <p className="text-sm text-white/80 line-clamp-2 mb-8 max-w-md drop-shadow-md">{subscribers[0].bio || `Deep dive into the creative process, debunking myths, and sharing strategies.`}</p>
                        
                        <div className="flex items-center gap-4">
                          <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300">
                            <Play className="w-6 h-6 text-black ml-1" fill="currentColor" />
                          </div>
                          <span className="text-base font-bold text-white underline decoration-white/50 underline-offset-4 group-hover:decoration-white transition-colors">View Profile</span>
                        </div>
                      </div>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </section>
        );
      })()}

{/* ═══════════ LATEST FROM THE BLOG ═══════════ */}
      {blogPosts.length > 0 && (
        <section className="min-h-[100dvh] flex flex-col justify-center py-20 md:py-28" data-testid="blog-section">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
            <>
              <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-3">
                <div>
                  <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: C.cyan }}>Resources</span>
                  <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mt-2" style={{ color: C.text }}><span className="text-gradient">Latest</span> from the Blog</h2>
                  <p className="text-sm mt-2 max-w-md" style={{ color: C.text2 }}>Tips, strategies, and industry insights to help you grow your podcast.</p>
                </div>
                <Link to="/blog" className="text-sm font-medium flex items-center gap-1 shrink-0" style={{ color: C.cyan }}>
                  View All Posts <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </>
            <div className="grid md:grid-cols-3 gap-6">
              {blogPosts.slice(0, 3).map((post, i) => (
                  <Link key={post.id || i} to={`/blog/${toBlogSlug(post.title)}`} className="card-hover rounded-2xl overflow-hidden block group" data-testid={`blog-card-${post.id}`}>
                    {post.image_url && (
                      <div className="h-44 overflow-hidden">
                        <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] px-2 py-0.5 rounded font-medium" style={{ background: `${C.purple}15`, color: C.purple }}>{post.category || 'General'}</span>
                        <span className="text-[10px]" style={{ color: C.muted }}>{post.read_time || '5 min read'}</span>
                      </div>
                      <h3 className="text-sm font-semibold mb-2 line-clamp-2 group-hover:underline" style={{ color: C.text }}>{post.title}</h3>
                      <p className="text-xs line-clamp-2 leading-relaxed" style={{ color: C.text2 }}>{post.excerpt}</p>

                    </div>
                  </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ═══════════ SPONSORED ADS ═══════════ */}
      {sponsoredAds.length > 0 && (
        <section className="min-h-[100dvh] flex flex-col justify-center py-16" data-testid="sponsored-section">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
            <>
              <div className="flex items-center justify-between mb-8">
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.muted }}>{t('home.sponsored')}</span>
                <Link to="/advertise" className="text-xs font-medium hover:opacity-80 transition-opacity" style={{ color: C.purple }}>
                  {t('home.advertiseWithUs')} <ArrowRight className="w-3 h-3 inline ml-1" />
                </Link>
              </div>
            </>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {sponsoredAds.slice(0, 3).map((ad, i) => (
                <div key={ad.id + '-ad-' + i}>
                  <a href={ad.cta_url || '#'} target="_blank" rel="noopener noreferrer"
                    onClick={() => { axios.post(`${API}/ads/track`, { ad_id: ad.id, event_type: 'click' }).catch(() => {}); }}
                    className="card-hover rounded-2xl overflow-hidden block group" data-testid={`sponsored-ad-${ad.id}`}>
                    {ad.image_url && (
                      <div className="h-36 overflow-hidden">
                        <img src={ad.image_url} alt={ad.name} className="w-full h-full object-cover transition-transform duration-500" loading="lazy" />
                      </div>
                    )}
                    <div className="p-5">
                      <h4 className="text-sm font-semibold mb-1" style={{ color: C.text }}>{ad.name}</h4>
                      <p className="text-xs leading-relaxed mb-3" style={{ color: C.text2 }}>{ad.description}</p>
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium"
                        style={{ color: ad.color || C.cyan }}>
                        {ad.cta_text || t('home.learnMore')} <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}



      {/* ═══════════ FOOTER ═══════════ */}
      <footer className="py-16 border-t" style={{ borderColor: C.border }} data-testid="footer">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
            <div className="sm:col-span-2 lg:col-span-2">
              <Link to="/" className="inline-block mb-5 logo-container">
                <img src="/logo-lockup.png" alt="Podcasters.Directory" className="h-12 object-contain logo-dark" />
                <img src="/logo-light.png" alt="Podcasters.Directory" className="h-12 object-contain logo-light" />
              </Link>
              <p className="text-sm leading-relaxed mb-6" style={{ color: C.text2 }}>{t('footer.desc')}</p>
              <SocialLinksComponent />
            </div>
            {[
              { title: t('footer.platform'), links: [[t('footer.browseDir'), '/directory'], ['Top 100', '/directory'], [t('footer.pricing'), '/pricing'], [t('blog.label'), '/blog']] },
              { title: t('footer.resources'), links: [[t('footer.howItWorks'), '#how-it-works'], [t('footer.forHosts'), '#'], [t('footer.forGuests'), '#'], [t('footer.faqLink', 'FAQ'), '#']] },
              { title: t('footer.company'), links: [[t('footer.about'), '#'], [t('footer.help'), '#'], [t('footer.privacy'), '/privacy', true], [t('footer.terms'), '/terms', true]] },
            ].map((col, i) => (
              <div key={i}>
                <h4 className="text-sm font-semibold mb-4" style={{ color: C.text }}>{col.title}</h4>
                <ul className="space-y-2.5">
                  {col.links.map(([label, href, external]) => (
                    <li key={label}>
                      {external ? (
                        <a href={href} target="_blank" rel="noopener noreferrer"
                          className="text-sm hover:opacity-80 transition-opacity" style={{ color: C.text2 }}
                          data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                          {label}
                        </a>
                      ) : (
                        <Link to={href} className="text-sm hover:opacity-80 transition-opacity" style={{ color: C.text2 }}>{label}</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: C.border }}>
            <p className="text-xs" style={{ color: C.muted }}>© 2026 Podcasters.Directory. {t('footer.rights')}</p>
            <div className="flex gap-6">
              <a href="/privacy" target="_blank" rel="noopener noreferrer"
                className="text-xs hover:opacity-80 transition-opacity" style={{ color: C.text2 }}
                data-testid="footer-bottom-privacy">
                {t('footer.privacy')}
              </a>
              <a href="/terms" target="_blank" rel="noopener noreferrer"
                className="text-xs hover:opacity-80 transition-opacity" style={{ color: C.text2 }}
                data-testid="footer-bottom-terms">
                {t('footer.terms')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
